import { Icon, IconSizeClasses } from '@eppendorf/vnls-react-components';
import type { FunctionComponentElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { AddOrEditNotiRuleModalScreens } from '$features/notifications/types';

import './notification-dialog-progress-indicator.scss';

export interface NotificationDialogProgressIndicatorProps {
  activeScreen: AddOrEditNotiRuleModalScreens;
  /** Called when the user clicks on a step that was already executed (i.e. it precedes the active step). */
  onStepClick?: (screen: AddOrEditNotiRuleModalScreens) => void;
}

type TranslationKey =
  | 'notificationRules.progressIndicator.devices'
  | 'notificationRules.progressIndicator.deviceTypes'
  | 'notificationRules.progressIndicator.condition'
  | 'notificationRules.progressIndicator.recipients'
  | 'notificationRules.progressIndicator.lifecycle'
  | 'notificationRules.progressIndicator.name';

type NotificationDialogProgressIndicatorStep = {
  key: string;
  labelKey: TranslationKey;
  icon: Parameters<typeof Icon>[0]['name'];
  screens: AddOrEditNotiRuleModalScreens[];
};

const steps: NotificationDialogProgressIndicatorStep[] = [
  {
    key: 'deviceTypes',
    labelKey: 'notificationRules.progressIndicator.deviceTypes',
    icon: 'overview-squares',
    screens: ['deviceTypeScreen'],
  },
  {
    key: 'devices',
    labelKey: 'notificationRules.progressIndicator.devices',
    icon: 'generic',
    screens: ['devicesScreen'],
  },
  {
    key: 'condition',
    labelKey: 'notificationRules.progressIndicator.condition',
    icon: 'trigger',
    screens: ['conditionScreen'],
  },
  {
    key: 'recipients',
    labelKey: 'notificationRules.progressIndicator.recipients',
    icon: 'user',
    screens: ['recipientsScreen'],
  },
  {
    key: 'lifecycle',
    labelKey: 'notificationRules.progressIndicator.lifecycle',
    icon: 'bell',
    screens: ['lifecycleScreen'],
  },
  {
    key: 'name',
    labelKey: 'notificationRules.progressIndicator.name',
    icon: 'edit',
    screens: ['nameScreen'],
  },
];

function getActiveStepIndex(activeScreen: AddOrEditNotiRuleModalScreens): number {
  const matchedIndex = steps.findIndex((step) => step.screens.includes(activeScreen));
  if (matchedIndex !== -1) return matchedIndex;

  // successScreen (and any other screen not represented in the stepper) is treated as "all done"
  return steps.length - 1;
}

export function NotificationDialogProgressIndicator({
  activeScreen,
  onStepClick,
}: NotificationDialogProgressIndicatorProps): FunctionComponentElement<NotificationDialogProgressIndicatorProps> {
  const { t } = useTranslation();
  const activeStepIndex = getActiveStepIndex(activeScreen);

  return (
    <div className="notification-progress-indicator flex flex__jc--center">
      {steps.map((step, index) => {
        const isCompleted = index < activeStepIndex;
        const isActive = index === activeStepIndex;
        const isClickable = isCompleted && !!onStepClick;

        return (
          <div className="notification-progress-indicator__step" key={step.key}>
            {index > 0 && (
              <div
                className={`notification-progress-indicator__connector${
                  index <= activeStepIndex
                    ? ' notification-progress-indicator__connector--completed'
                    : ''
                }`}
              />
            )}
            <button
              type="button"
              className="notification-progress-indicator__step-content flex flex__dir--column flex__ai--center"
              onClick={isClickable ? () => onStepClick?.(step.screens[0]) : undefined}
              disabled={!isClickable}
              aria-current={isActive ? 'step' : undefined}
            >
              <div
                className={`notification-progress-indicator__circle${
                  isCompleted ? ' notification-progress-indicator__circle--completed' : ''
                }${isActive ? ' notification-progress-indicator__circle--active' : ''}${
                  isClickable ? ' notification-progress-indicator__circle--clickable' : ''
                }`}
              >
                <Icon
                  name={step.icon}
                  size={IconSizeClasses.XSmall}
                  className={
                    // eslint-disable-next-line no-nested-ternary -- readable given the 3 mutually exclusive step states
                    isActive ? 'bg-white' : isCompleted ? 'bg-blue-500' : 'bg-gray-900'
                  }
                />
              </div>
              <span
                className={`notification-progress-indicator__label${
                  isActive ? ' font-weight-semibold' : ''
                }`}
              >
                {t(step.labelKey)}
              </span>
            </button>
          </div>
        );
      })}
    </div>
  );
}
